import React, { useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowDownIcon,
  FlexRow,
  InfoIcon,
  SvgIcon,
  TableData,
} from '@make-software/cspr-design';
import { BodyText, Button, Table, Tooltip } from '@block-explorer/ui';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsNameTableData from '../details-name-table-data/details-name-table-data';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import {
  DetailsAccordionAssociatedKeysItem,
  TokenWrapper,
} from '../details-accordion-associated-keys/details-accordion-associated-keys-item';
import { InfoSvgIcon } from '../specialized-svg-icons/specialized-svg-icons';
import { useCreateAccountsInfoGetter } from '../../hooks';
import { deriveHashListByKeysFromObject } from '../../utils/account';
import { AssociatedKey } from '@block-explorer/entities';
import ExpandCollapsedButton from '../expand-collapsed/expand-collapsed-button.tsx';
import { MANAGE_ASSOCIATED_KEYS_PATH } from '../../router/paths';
import { useCustomHistory } from '../../router/navigation';

const MAXIMUM_VISIBLE_ROWS = 5;

const ButtonContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    padding: '12px 20px 0',
  })
);

export const StyledButton = styled(Button)(({ theme }) => ({
  width: 220,
}));

interface AssociatedKeysTableProps {
  loading: boolean;
  withManageKeysButton?: boolean;
  associatedKeys: AssociatedKey[];
  actionThresholds:
    | {
        deployment: number;
        key_management: number;
      }
    | undefined;
}

const AssociatedKeysTable = ({
  associatedKeys = [],
  actionThresholds,
  loading,
  withManageKeysButton = false,
}: AssociatedKeysTableProps) => {
  const { t } = useTranslation();
  const { push } = useCustomHistory();
  const [isCollapsed, setCollapsed] = useState<boolean>(true);

  const hashList = deriveHashListByKeysFromObject(associatedKeys, [
    'account_hash',
  ]);
  const { getAccountInfoByHash, loading: loadingAllAccountsInfo } =
    useCreateAccountsInfoGetter([], hashList);

  const sortedAssociatedKeys = (associatedKeys || []).sort(
    (a, b) => b.weight - a.weight
  );

  const handleExpandList = () => {
    setCollapsed(!isCollapsed);
  };
  return (
    <Table
      paddingBottom={12}
      renderData={() => (
        <>
          <DetailsTableRow>
            <DetailsNameTableData>
              <Trans t={t}>Thresholds</Trans>
            </DetailsNameTableData>
            <DetailsValueTableData>
              <FlexRow itemsSpacing={19}>
                <TokenWrapper>
                  <FlexRow itemsSpacing={6} align="center">
                    <BodyText size={3} variation={'black'}>
                      {t('Transaction submission')}:
                    </BodyText>
                    <BodyText size={2} variation={'black'}>
                      {actionThresholds?.deployment}
                    </BodyText>
                    <Tooltip
                      tooltipContent={t(
                        'Signature weight required to submit a transaction'
                      )}
                    >
                      <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 6 }} />
                    </Tooltip>
                  </FlexRow>
                </TokenWrapper>
                <TokenWrapper>
                  <FlexRow itemsSpacing={6} align="center">
                    <BodyText size={3} variation={'black'}>
                      <Trans t={t}>Key management:</Trans>
                    </BodyText>
                    <BodyText size={2} variation={'black'}>
                      {actionThresholds?.key_management}
                    </BodyText>
                    <Tooltip
                      tooltipContent={t(
                        'Signature weight required to modify associated key settings'
                      )}
                    >
                      <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 6 }} />
                    </Tooltip>
                  </FlexRow>
                </TokenWrapper>
              </FlexRow>
            </DetailsValueTableData>
          </DetailsTableRow>

          {sortedAssociatedKeys
            .filter((associatedKey, i) =>
              isCollapsed ? i < MAXIMUM_VISIBLE_ROWS : true
            )
            .map((associatedKey, index) => (
              <DetailsAccordionAssociatedKeysItem
                key={associatedKey.account_hash}
                data={associatedKey}
                index={index}
                loading={loading || loadingAllAccountsInfo}
                getAccountInfo={getAccountInfoByHash}
              />
            ))}
          {sortedAssociatedKeys.length > MAXIMUM_VISIBLE_ROWS && (
            <DetailsTableRow>
              <TableData>
                <ExpandCollapsedButton
                  onExpand={handleExpandList}
                  expandedLabel={<Trans t={t}>Hide keys </Trans>}
                  collapsedLabel={
                    <>
                      <Trans t={t}>See all </Trans>
                      {associatedKeys?.length}
                      <Trans t={t}> keys</Trans>
                    </>
                  }
                />
              </TableData>
            </DetailsTableRow>
          )}
          {withManageKeysButton ? (
            <DetailsTableRow>
              <ButtonContainer>
                <StyledButton
                  color="secondaryBlue"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    push(MANAGE_ASSOCIATED_KEYS_PATH, { allowedGoBack: true });
                  }}
                >
                  <Trans t={t}>Manage associated keys</Trans>
                </StyledButton>
              </ButtonContainer>
            </DetailsTableRow>
          ) : null}
        </>
      )}
    />
  );
};

export default AssociatedKeysTable;
